/**
 * AP-32 13.3 — **Das Positionsraster als reine Datenoperationen.**
 *
 * Einrücken, Herausheben, Verschieben mitsamt Kindern, Einfügen, Entfernen —
 * getrennt von der Darstellung, damit sie prüfbar sind ohne ein DOM. Die Zeile
 * hält ihre Zahlen als **Text**, wie sie getippt werden („2,5"); gelesen wird
 * erst beim Rechnen und Speichern.
 */

import {
  berechneBeleg,
  geldText,
  type CalcResult,
  type DocumentLineType,
  type PositionDraft,
  type PriceMode,
  type SubPositionMode,
  type TaxProcedure,
} from '@symphenia/shared-types'
import type { DocumentLineView, LineFileRef, LineInput } from '../types/document.types'
import { verschiebeMehrereVor } from '@/components/ziehen/ziehen'

export interface GridLine {
  /** Stabil für React und die Auswahl — die ID einer gespeicherten Zeile, sonst ein Platzhalter. */
  key: string
  id?: string
  lineType: DocumentLineType
  articleId: string | null
  articleNumber: string
  articleName: string
  unitCode: string
  /** Nachtrag 2: Alternativmenge als Text und Einheit — Angabe, kein Rechenweg. */
  altQuantity: string
  altUnitCode: string
  /** Alternativmenge je Einheit der Menge — hält die Alternativmenge beim Tippen der Menge nach. Nur im Browser. */
  altVerhaeltnis: number | null
  description: string
  /** Nachtrag 12 (N-93): Positionsbeschreibung — einleitender Text vor der Position. */
  introText: string
  /** Nachtrag 12 (N-94): Abweichungen vom Belegkopf; leer heißt „wie im Beleg". */
  customerReference: string
  /** `JJJJ-MM-TT` — leer, wenn der Liefertermin des Belegs gilt. */
  deliveryDate: string
  deliveryNote: string
  quantity: string
  unitPrice: string
  priceUnit: number
  discountPercent: string
  taxRate: string
  isOptional: boolean
  isAlternative: boolean
  subMode: SubPositionMode | null
  projectId: string | null
  projectNumber: string | null
  projectPositionId: string | null
  projectPositionNumber: string | null
  projectPositionName: string | null
  hasImage: boolean
  /** Kennung des Fotostands (N-4) — Schlüssel des Bild-Caches. */
  imageVersion: string | null
  /** Nachtrag 4: Dateien an der Position — nur Anzeige, nicht Teil von `PUT …/lines`. */
  files: LineFileRef[]
  /**
   * N-103: Die Zeile kommt aus der Vorschau des Einfügens. Beim Speichern holt der Server
   * Herkunft, Foto und Verknüpfung aus diesem Ablage-Eintrag. Nur im Browser.
   */
  ablage?: { itemId: string; index: number } | null
  /** N-103: Ein Foto aus der Ablage kommt beim Speichern mit — nur Anzeige. */
  fotoAusAblage?: boolean
  /** N-104: Foto und Dateien, die auf „Speichern" warten. Nur im Browser. */
  fotoNeu?: File | null
  fotoWeg?: boolean
  dateienNeu?: File[]
  dateienWeg?: string[]
  children: GridLine[]
}

/** N-104: Kennung einer wartenden Datei in der Anzeige — `neu:<Stelle in dateienNeu>`. */
export const wartendeDateiId = (index: number) => `neu:${index}`
export const wartendeDatei = (id: string): number | null => (id.startsWith('neu:') ? Number(id.slice(4)) : null)

/** N-104: Wartet an der Zeile ein Foto oder eine Datei auf „Speichern"? */
export const hatOffeneAnhaenge = (z: GridLine) => !!(z.fotoNeu || z.fotoWeg || z.dateienNeu?.length || z.dateienWeg?.length)

let zaehler = 0
export const neuerSchluessel = () => `neu-${Date.now()}-${++zaehler}`

/** Deutsche und englische Schreibweise: „1.234,5" und „1234.5". Leer → null, Unsinn → NaN. */
export function leseZahl(text: string | number | null | undefined): number | null {
  if (text === null || text === undefined) return null
  if (typeof text === 'number') return text
  // Nachtrag 2: „10%" ist eine Zahl — sonst hielte der Zahlungsplan die Zeile für den Rest.
  const t = text.trim().replace(/\s/g, '').replace(/%$/, '')
  if (!t) return null
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t
  return /^-?\d*(\.\d*)?$/.test(normal) ? Number(normal) : Number.NaN
}

export function zahlText(wert: number | null | undefined, maxStellen = 3): string {
  if (wert === null || wert === undefined) return ''
  return new Intl.NumberFormat(navigator.language, { maximumFractionDigits: maxStellen, useGrouping: false }).format(wert)
}

export function ausAnsicht(zeilen: readonly DocumentLineView[]): GridLine[] {
  const eine = (z: DocumentLineView): GridLine => ({
    key: z.id,
    id: z.id,
    lineType: z.lineType,
    articleId: z.articleId,
    articleNumber: z.articleNumber ?? '',
    articleName: z.articleName ?? '',
    unitCode: z.unitCode ?? '',
    altQuantity: zahlText(z.altQuantity ?? null, 3),
    altUnitCode: z.altUnitCode ?? '',
    altVerhaeltnis: z.altQuantity !== null && z.altQuantity !== undefined && z.quantity ? z.altQuantity / z.quantity : null,
    description: z.description ?? '',
    introText: z.introText ?? '',
    customerReference: z.customerReference ?? '',
    deliveryDate: z.deliveryDate?.slice(0, 10) ?? '',
    deliveryNote: z.deliveryNote ?? '',
    quantity: zahlText(z.quantity, 3),
    unitPrice: zahlText(z.unitPrice ?? null, 2),
    priceUnit: z.priceUnit,
    discountPercent: zahlText(z.discountPercent ?? null, 2),
    taxRate: zahlText(z.taxRate, 2),
    isOptional: z.isOptional,
    isAlternative: z.isAlternative,
    subMode: z.subMode,
    projectId: z.projectId,
    projectNumber: z.projectNumber,
    projectPositionId: z.projectPositionId,
    projectPositionNumber: z.projectPositionNumber,
    projectPositionName: z.projectPositionName,
    hasImage: z.hasImage,
    imageVersion: z.imageVersion,
    files: z.files ?? [],
    children: (z.children ?? []).map(eine),
  })
  return zeilen.map(eine)
}

export function neueZeile(
  lineType: DocumentLineType = 'ARTICLE',
  /** Was die neue Zeile vom Belegkopf mitbekommt (N-101: auch die Projektposition). */
  erbe: Partial<Pick<GridLine, 'projectId' | 'projectNumber' | 'projectPositionId' | 'projectPositionNumber' | 'projectPositionName'>> = {},
): GridLine {
  return {
    key: neuerSchluessel(),
    lineType,
    articleId: null,
    articleNumber: '',
    articleName: '',
    unitCode: '',
    altQuantity: '',
    altUnitCode: '',
    altVerhaeltnis: null,
    description: '',
    introText: '',
    customerReference: '',
    deliveryDate: '',
    deliveryNote: '',
    quantity: lineType === 'ARTICLE' ? '1' : '',
    unitPrice: '',
    priceUnit: 1,
    discountPercent: '',
    taxRate: '',
    isOptional: false,
    isAlternative: false,
    subMode: null,
    projectId: erbe.projectId ?? null,
    projectNumber: erbe.projectNumber ?? null,
    projectPositionId: erbe.projectPositionId ?? null,
    projectPositionNumber: erbe.projectPositionNumber ?? null,
    projectPositionName: erbe.projectPositionName ?? null,
    hasImage: false,
    imageVersion: null,
    files: [],
    children: [],
  }
}

/**
 * N-6 — „+ Unterposition": eine neue Zeile als **letzte** Unterposition einer
 * Artikel-Hauptposition. Unverändert, wenn die Zeile keine Hauptposition vom Typ
 * Position ist (5.2: genau eine Ebene).
 */
export function neuesKind(zeilen: readonly GridLine[], elternKey: string, neu: GridLine): GridLine[] {
  return zeilen.map((z) =>
    z.key === elternKey && z.lineType === 'ARTICLE'
      ? { ...z, subMode: z.subMode ?? 'SUM_OF_CHILDREN', children: [...z.children, { ...neu, projectId: neu.projectId ?? z.projectId, projectNumber: neu.projectNumber ?? z.projectNumber }] }
      : z,
  )
}

/**
 * Raster → Eingang für `PUT …/lines`. **Ohne Preisrecht gehen keine Preisfelder
 * hinaus** — der Server behielte sie ohnehin (10.3), aber die Maske soll nicht
 * einmal so tun, als schriebe sie welche.
 */
export function zuEingabe(zeilen: readonly GridLine[], preise: boolean): LineInput[] {
  const eine = (z: GridLine): LineInput => {
    const menge = leseZahl(z.quantity)
    const satz = leseZahl(z.taxRate)
    const eingang: LineInput = {
      ...(z.id && { id: z.id }),
      ...(!z.id && z.ablage && { clipboard: z.ablage }),
      lineType: z.lineType,
      articleId: z.articleId,
      articleNumber: z.articleNumber || null,
      articleName: z.articleName || null,
      unitCode: z.unitCode || null,
      altQuantity: (() => {
        const alt = leseZahl(z.altQuantity)
        return alt === null || Number.isNaN(alt) ? null : alt
      })(),
      altUnitCode: z.altUnitCode.trim() || null,
      description: z.description || null,
      introText: z.introText.trim() || null,
      customerReference: z.customerReference.trim() || null,
      deliveryDate: z.deliveryDate || null,
      deliveryNote: z.deliveryNote.trim() || null,
      quantity: menge ?? (z.lineType === 'ARTICLE' ? 1 : 0),
      taxRate: satz === null || Number.isNaN(satz) ? null : satz,
      isOptional: z.isOptional,
      isAlternative: z.isAlternative,
      subMode: z.children.length ? (z.subMode ?? 'SUM_OF_CHILDREN') : null,
      projectId: z.projectId,
      projectPositionId: z.projectPositionId,
    }
    if (preise) {
      eingang.unitPrice = leseZahl(z.unitPrice) ?? 0
      eingang.priceUnit = z.priceUnit
      const rabatt = leseZahl(z.discountPercent)
      eingang.discountPercent = rabatt === null || Number.isNaN(rabatt) ? null : rabatt
    }
    if (z.children.length) eingang.children = z.children.map(eine)
    return eingang
  }
  return zeilen.map(eine)
}

/** Flache Sicht fürs Rendern: jede Zeile mit Tiefe und Eltern. */
export function flach(zeilen: readonly GridLine[]): { zeile: GridLine; tiefe: 0 | 1; eltern: GridLine | null }[] {
  return zeilen.flatMap((z) => [
    { zeile: z, tiefe: 0 as const, eltern: null },
    ...z.children.map((k) => ({ zeile: k, tiefe: 1 as const, eltern: z })),
  ])
}

export function finde(zeilen: readonly GridLine[], key: string): { zeile: GridLine; eltern: GridLine | null } | null {
  for (const z of zeilen) {
    if (z.key === key) return { zeile: z, eltern: null }
    const k = z.children.find((c) => c.key === key)
    if (k) return { zeile: k, eltern: z }
  }
  return null
}

export function aendere(zeilen: readonly GridLine[], key: string, patch: Partial<GridLine>): GridLine[] {
  return zeilen.map((z) =>
    z.key === key
      ? { ...z, ...patch }
      : z.children.some((c) => c.key === key)
        ? { ...z, children: z.children.map((c) => (c.key === key ? { ...c, ...patch } : c)) }
        : z,
  )
}

/**
 * Alt+→ — die Zeile wird Unterposition der Hauptposition darüber (5.2: genau
 * eine Ebene). Unverändert, wenn es nicht geht: keine Hauptposition darüber,
 * die Zeile hat selbst Kinder, oder die Typen passen nicht.
 */
export function einruecken(zeilen: readonly GridLine[], key: string): GridLine[] {
  const i = zeilen.findIndex((z) => z.key === key)
  if (i <= 0) return [...zeilen]
  const zeile = zeilen[i]
  const eltern = zeilen[i - 1]
  if (zeile.children.length) return [...zeilen]
  if (eltern.lineType !== 'ARTICLE') return [...zeilen]
  if (zeile.lineType !== 'ARTICLE' && zeile.lineType !== 'TEXT') return [...zeilen]
  const neu = [...zeilen]
  neu.splice(i, 1)
  neu[i - 1] = { ...eltern, subMode: eltern.subMode ?? 'SUM_OF_CHILDREN', children: [...eltern.children, zeile] }
  return neu
}

/** Alt+← — die Unterposition wird Hauptposition direkt hinter ihrer Gruppe. */
export function ausruecken(zeilen: readonly GridLine[], key: string): GridLine[] {
  const i = zeilen.findIndex((z) => z.children.some((c) => c.key === key))
  if (i < 0) return [...zeilen]
  const eltern = zeilen[i]
  const kind = eltern.children.find((c) => c.key === key)!
  const rest = eltern.children.filter((c) => c.key !== key)
  const neu = [...zeilen]
  neu[i] = { ...eltern, children: rest, subMode: rest.length ? eltern.subMode : null }
  neu.splice(i + 1, 0, kind)
  return neu
}

/** Alt+↑/↓ — eine Hauptposition wandert **mitsamt ihren Kindern**; ein Kind innerhalb seiner Gruppe. */
export function verschiebe(zeilen: readonly GridLine[], key: string, richtung: -1 | 1): GridLine[] {
  const i = zeilen.findIndex((z) => z.key === key)
  if (i >= 0) {
    const j = i + richtung
    if (j < 0 || j >= zeilen.length) return [...zeilen]
    const neu = [...zeilen]
    ;[neu[i], neu[j]] = [neu[j], neu[i]]
    return neu
  }
  return zeilen.map((z) => {
    const k = z.children.findIndex((c) => c.key === key)
    if (k < 0) return z
    const j = k + richtung
    if (j < 0 || j >= z.children.length) return z
    const kinder = [...z.children]
    ;[kinder[k], kinder[j]] = [kinder[j], kinder[k]]
    return { ...z, children: kinder }
  })
}

/**
 * N-72, N-69 neu — **Ziehen**: `keys` wandern geschlossen und in ihrer
 * Reihenfolge vor `vorKey` (`null` = ans Ende der Ebene). Die Ebene gibt die
 * gegriffene Zeile `key` vor: eine Hauptposition bewegt sich unter den
 * Hauptpositionen und nimmt ihre Unterpositionen mit, eine Unterposition nur
 * innerhalb ihrer Gruppe (N-65). Aus `keys` zählen nur die derselben Ebene bzw.
 * Gruppe. Unverändert — **dieselbe Referenz** —, wenn `vorKey` nicht in dieser
 * Ebene liegt oder die Reihenfolge gleich bliebe (N-68).
 */
export function verschiebeVor(zeilen: GridLine[], key: string, keys: readonly string[], vorKey: string | null): GridLine[] {
  const reihe = (liste: readonly GridLine[]) => liste.map((z) => z.key)
  const ordne = (liste: GridLine[]): GridLine[] | null => {
    const alt = reihe(liste)
    if (vorKey !== null && !alt.includes(vorKey)) return null
    const mit = keys.includes(key) ? keys : [key]
    const neu = verschiebeMehrereVor(alt, mit.filter((k) => alt.includes(k)), vorKey)
    if (neu === alt) return null
    const nach = new Map(liste.map((z) => [z.key, z]))
    return neu.map((k) => nach.get(k)!)
  }
  if (zeilen.some((z) => z.key === key)) return ordne(zeilen) ?? zeilen
  const i = zeilen.findIndex((z) => z.children.some((c) => c.key === key))
  if (i < 0) return zeilen
  const kinder = ordne(zeilen[i].children)
  if (!kinder) return zeilen
  const kopie = [...zeilen]
  kopie[i] = { ...zeilen[i], children: kinder }
  return kopie
}

/** Neue Zeile hinter der markierten — hinter ein Kind als Geschwister, hinter eine Hauptposition nach ihrer Gruppe. */
/**
 * N-103: Positionen aus der Ablage als **ungespeicherte** Zeilen. Der Steuersatz bleibt leer —
 * ihn löst der Zielbeleg beim Speichern neu auf (5.8 Schritt 4).
 */
export function ausAblage(p: PositionDraft, itemId: string, fotos: readonly boolean[] = []): GridLine {
  const eine = (d: PositionDraft, index: number): GridLine => {
    const basis = neueZeile(d.lineType)
    return {
      ...basis,
      articleId: d.articleId ?? null,
      articleNumber: d.articleNumber ?? '',
      articleName: d.articleName ?? '',
      unitCode: d.unitCode ?? '',
      altQuantity: zahlText(d.altQuantity ?? null, 3),
      altUnitCode: d.altUnitCode ?? '',
      altVerhaeltnis: d.altQuantity !== null && d.altQuantity !== undefined && d.quantity ? d.altQuantity / d.quantity : null,
      description: d.description ?? '',
      introText: d.introText ?? '',
      customerReference: d.customerReference ?? '',
      deliveryDate: d.deliveryDate?.slice(0, 10) ?? '',
      deliveryNote: d.deliveryNote ?? '',
      quantity: d.quantity !== undefined ? zahlText(d.quantity, 3) : basis.quantity,
      unitPrice: zahlText(d.unitPrice ?? null, 2),
      priceUnit: d.priceUnit ?? 1,
      discountPercent: zahlText(d.discountPercent ?? null, 2),
      taxRate: '',
      isOptional: d.isOptional ?? false,
      isAlternative: d.isAlternative ?? false,
      subMode: d.subMode ?? null,
      projectId: d.projectId ?? null,
      projectNumber: d.projectNumber ?? null,
      projectPositionId: d.projectPositionId ?? null,
      projectPositionNumber: d.projectPositionNumber ?? null,
      projectPositionName: d.projectPositionName ?? null,
      ablage: { itemId, index },
      fotoAusAblage: !!fotos[index],
    }
  }
  return { ...eine(p, 0), children: (p.children ?? []).map((k, i) => eine(k, i + 1)) }
}

/** N-103: Hauptpositionen hinter die Gruppe der markierten Zeile — eine markierte Unterposition zählt als ihre Hauptposition. */
export function fuegeGruppenEin(zeilen: readonly GridLine[], key: string | null, neue: readonly GridLine[]): GridLine[] {
  const i = key ? zeilen.findIndex((z) => z.key === key || z.children.some((c) => c.key === key)) : -1
  if (i < 0) return [...zeilen, ...neue]
  return [...zeilen.slice(0, i + 1), ...neue, ...zeilen.slice(i + 1)]
}

export function einfuegenNach(zeilen: readonly GridLine[], key: string | null, neu: GridLine): GridLine[] {
  if (!key) return [...zeilen, neu]
  const i = zeilen.findIndex((z) => z.key === key)
  if (i >= 0) {
    const kopie = [...zeilen]
    kopie.splice(i + 1, 0, neu)
    return kopie
  }
  return zeilen.map((z) => {
    const k = z.children.findIndex((c) => c.key === key)
    if (k < 0) return z
    if (neu.lineType !== 'ARTICLE' && neu.lineType !== 'TEXT') return z
    const kinder = [...z.children]
    kinder.splice(k + 1, 0, neu)
    return { ...z, children: kinder }
  })
}

export function entferne(zeilen: readonly GridLine[], keys: ReadonlySet<string>): GridLine[] {
  return zeilen
    .filter((z) => !keys.has(z.key))
    .map((z) => {
      const kinder = z.children.filter((c) => !keys.has(c.key))
      return kinder.length === z.children.length ? z : { ...z, children: kinder, subMode: kinder.length ? z.subMode : null }
    })
}

/**
 * **Papierkorb der Belegmaske** (AP-32 Nachtrag 12, N-102). Eine gelöschte Zeile
 * merkt sich, **wo** sie stand — nur so kehrt sie an ihre Stelle zurück und nicht
 * ans Ende.
 *
 * Anders als in der Stückliste liegt dieser Papierkorb im Browser: Die Positionen
 * eines Belegs werden als Ganzes gespeichert (19.2-9), eine einzelne Zeile gibt es
 * auf dem Server erst nach dem Speichern. Er hält deshalb bis zum Verlassen der
 * Maske, nicht 30 Tage.
 */
export interface GeloeschteZeile {
  /** Eigene Kennung des Eintrags — dieselbe Zeile kann mehrmals gelöscht werden. */
  trashId: string
  /** Was im Hinweis und im Papierkorb steht. */
  name: string
  /** Die Zeile samt ihren Unterpositionen. */
  zeile: GridLine
  /** Hauptposition: `null`. Unterposition: der Schlüssel ihrer Hauptposition. */
  elternKey: string | null
  /** Stelle in ihrer Ebene, als sie gelöscht wurde. */
  index: number
  geloeschtAm: string
}

let trashZaehler = 0

/**
 * Entfernt die Zeilen **und** legt sie in den Papierkorb. Eine Hauptposition nimmt
 * ihre Unterpositionen mit: Sie hängen in `children` und bekommen darum keinen
 * eigenen Eintrag — sonst käme ein Kind beim Wiederherstellen doppelt zurück.
 */
export function entferneMitAblage(
  zeilen: readonly GridLine[],
  keys: ReadonlySet<string>,
  benenne: (z: GridLine) => string,
): { zeilen: GridLine[]; geloescht: GeloeschteZeile[] } {
  const jetzt = new Date().toISOString()
  const geloescht: GeloeschteZeile[] = []
  const merke = (z: GridLine, elternKey: string | null, index: number) =>
    geloescht.push({ trashId: `t${++trashZaehler}`, name: benenne(z), zeile: z, elternKey, index, geloeschtAm: jetzt })

  zeilen.forEach((z, i) => {
    if (keys.has(z.key)) {
      merke(z, null, i)
      return
    }
    z.children.forEach((k, j) => keys.has(k.key) && merke(k, z.key, j))
  })
  return { zeilen: entferne(zeilen, keys), geloescht }
}

/**
 * Legt eine Zeile an ihre alte Stelle zurück. Ist ihre Hauptposition inzwischen
 * selbst gelöscht, kommt sie als Hauptposition ans Ende — verloren geht sie nicht.
 */
export function stelleWiederHer(zeilen: readonly GridLine[], eintrag: GeloeschteZeile): GridLine[] {
  if (eintrag.elternKey === null) {
    const neu = [...zeilen]
    neu.splice(Math.min(eintrag.index, neu.length), 0, eintrag.zeile)
    return neu
  }
  const eltern = zeilen.find((z) => z.key === eintrag.elternKey)
  if (!eltern) return [...zeilen, { ...eintrag.zeile, children: [] }]
  return zeilen.map((z) => {
    if (z.key !== eintrag.elternKey) return z
    const kinder = [...z.children]
    kinder.splice(Math.min(eintrag.index, kinder.length), 0, eintrag.zeile)
    return { ...z, children: kinder, subMode: z.subMode ?? 'SUM_OF_CHILDREN' }
  })
}

/**
 * Rechnet das Raster live — **mit dem Rechenkern des Servers** (19.2-29). Nicht
 * lesbare Zahlen zählen als 0; die Prüfung macht der Server beim Speichern.
 */
export function rechne(
  zeilen: readonly GridLine[],
  kopf: { priceMode: PriceMode; taxProcedure: TaxProcedure; documentDiscountPercent: number | null },
  vorgabeSatz: number,
): CalcResult {
  const sicher = (t: string, fallback: number) => {
    const n = leseZahl(t)
    return n === null || Number.isNaN(n) ? fallback : n
  }
  return berechneBeleg({
    ...kopf,
    lines: flach(zeilen).map(({ zeile: z, eltern }) => ({
      key: z.key,
      parentKey: eltern?.key ?? null,
      lineType: z.lineType,
      quantity: sicher(z.quantity, 0),
      unitPrice: sicher(z.unitPrice, 0),
      priceUnit: z.priceUnit,
      discountPercent: leseZahl(z.discountPercent) ?? null,
      taxRate: sicher(z.taxRate, vorgabeSatz),
      subMode: z.children.length ? (z.subMode ?? 'SUM_OF_CHILDREN') : null,
      isOptional: z.isOptional,
      isAlternative: z.isAlternative,
    })),
  })
}

/**
 * Ein Betrag des Belegs: `1.234,50`, **ohne Währungszeichen** (Auftraggeber
 * 20.9.2026, Nachtrag 12). Welche Währung gilt, sagt der Belegkopf einmal —
 * hinter jedem Betrag wiederholt, verstellt das Zeichen nur die Zahlen.
 *
 * Die Währung bleibt als Parameter stehen, damit die Aufrufer unverändert
 * lesbar sind und ein späteres „doch mit Zeichen" an **einer** Stelle
 * zurückzunehmen ist (`geldMitWaehrung`).
 */
export function formatBetrag(wert: number | null | undefined, _waehrung?: string): string {
  return geldText(wert)
}

/** Ein Belegdatum `JJJJ-MM-TT` in der Schreibweise des Benutzers — ohne den Tag zu verschieben. */
export function formatDatum(wert: string | null | undefined): string {
  if (!wert) return ''
  return new Date(`${wert.slice(0, 10)}T00:00:00Z`).toLocaleDateString(navigator.language, { timeZone: 'UTC' })
}

export function heuteText(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}
